// Client-side error reporting to Better Stack (#180 — dropped Sentry, the ecosystem now runs all
// observability on Better Stack). This is the single client error transport: initialised once from
// the client entry, gated on the Better Stack source token + ingest endpoint, and a safe no-op when
// unconfigured (local dev with no token behaves exactly as the old "no DSN, no Sentry" path did).
//
// We use @logtail/browser (Better Stack's browser client), not the OpenTelemetry OTLP exporter the
// server apps (walkup, identity) use. The browser package posts to the per-source Logtail ingest
// host with a write-only source token that is designed to ship in client JS — the same posture as a
// Sentry DSN (Better Stack's own Next.js integration exposes it via a NEXT_PUBLIC_ env var).
//
// The Better Stack config (source token + ingest URL) is read from the VITE_-prefixed env in the
// client entry and passed into initErrorReporter — this module never touches import.meta, so it
// stays plain-importable from the store and from tests. Don't add a second client error reporter or
// a parallel init path — route everything through reportError() so there is one place that knows
// about Better Stack.
import { Logtail } from "@logtail/browser";
import type { ZodIssue } from "zod";

export interface ErrorReporterConfig {
  sourceToken?: string;
  ingestUrl?: string;
}

/**
 * Structured context attached to a client error report. Kept small and named so the call sites
 * don't drift on field shapes: `feature` tags the surface that failed, `issues` carries zod
 * validation issues when a parse failed. Anything else rides in `extra`.
 */
export interface ErrorContext {
  feature?: string;
  issues?: ZodIssue[];
  extra?: Record<string, unknown>;
}

// Module-private singleton, set once by initErrorReporter(). Exposed for tests via the seam below.
let client: Logtail | null = null;

/**
 * Initialise the Better Stack browser client from config read at the call site (the client entry).
 * No-op unless BOTH the source token and ingest URL are present, so an unconfigured build (local
 * dev) reports nothing and never throws. Idempotent: a second call with the client already set does
 * nothing.
 */
export function initErrorReporter(config: ErrorReporterConfig): void {
  if (client) return;
  const { sourceToken, ingestUrl } = config;
  if (!sourceToken || !ingestUrl) return;
  client = new Logtail(sourceToken, { endpoint: ingestUrl });
}

/**
 * Report a client error to Better Stack with structured context. A no-op when the reporter is
 * unconfigured. Never throws and never lets a telemetry failure crash the app — a rejected
 * error()/flush() is swallowed (logged to the console in dev so it isn't completely silent).
 */
export function reportError(error: unknown, context: ErrorContext = {}): void {
  if (!client) return;
  const message = error instanceof Error ? error.message : String(error);
  const payload: Record<string, unknown> = {
    ...context.extra,
    ...(context.feature ? { feature: context.feature } : {}),
    ...(context.issues !== undefined ? { issues: context.issues } : {}),
    ...(error instanceof Error && error.stack ? { stack: error.stack } : {}),
  };
  client
    .error(message, payload)
    .then(() => client?.flush())
    .catch((reportingError: unknown) => {
      // Vite statically replaces process.env.NODE_ENV at build time, so this is a constant in the
      // bundle and doesn't pull a Node global into the browser. Keeps the failed-report noise out of
      // production while still surfacing it in dev/test.
      if (process.env.NODE_ENV !== "production") {
        console.error("Failed to report error to Better Stack", reportingError);
      }
    });
}

/**
 * Test seam — inject a fake client (or null to reset). Production code never calls this; it exists
 * so tests can assert reportError forwards to the client without a real network call.
 */
export function __setErrorReporterClientForTests(fake: Logtail | null): void {
  client = fake;
}
